import {useState, useCallback, useMemo, useEffect, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useAppConfig, DEFAULT_PAGINATION_CONFIG} from '@/hooks/use-app-config'
import {useFilterStatePersistence} from '@/hooks/use-filter-state-persistence'
import {unpackEntityLinkId, unpackEntityLinkIdsCsv} from '@/lib/entity-link-packing'
import type {EndpointContract, EndpointContractFilterDescriptor} from '@/types/endpoint-contract'

export type SortDirection = 'asc' | 'desc'

// URL param names — technical identifiers, never localised.
const PARAM_SEARCH = 'search'
const PARAM_Q_DEFAULT = 'q'
const PARAM_PAGE = 'page'
const PARAM_SIZE = 'size'
const PARAM_SORT_BY = 'sortBy'
const PARAM_SORT_DIR = 'sortDir'
const PARAM_ATTR_PREFIX = 'attr.'

export const SortDir = {
    ASC: 'asc',
    DESC: 'desc',
} as const satisfies Record<string, SortDirection>

/**
 * Free-text search string plus the per-filter value map driven by the
 * {@link EndpointContract.filter} declaration. Keys match the contract's
 * filter descriptor keys (e.g., `STATUS`, `PARTY_TYPE`). Values are kept
 * as strings or string arrays — the runtime serialiser in
 * `build-filter-query` turns them into URL params.
 */
export interface SearchAndFilters {
    q: string
    filters: Record<string, string | string[]>
    dynamicAttributes: Record<string, string | string[]>
}

/**
 * Initial value for client-side {@link UnifiedSearchBar} callers that manage
 * their own state with {@code useState}. Server-paginated callers do not need
 * this — {@link useServerPagination} reads its initial state from the URL.
 *
 * Callers replace the value via {@code setState} (immutable update); never
 * mutate the object in place.
 */
export const EMPTY_SEARCH_AND_FILTERS: SearchAndFilters = {
    q: '',
    filters: {},
    dynamicAttributes: {},
}

interface UseServerPaginationOptionsBase {
    defaultSortDir?: SortDirection
    defaultPageSize?: number
    sortable?: boolean
    /**
     * URL param namespace prefix — when set, every param is read/written as
     * `{namespace}.{param}`. Used to host multiple bars on one page (§5.7).
     */
    namespace?: string
}

/**
 * Options for the unified-search form — callers pass a typed endpoint contract
 * and {@code defaultSortBy} is constrained to that endpoint's declared sort
 * paths.
 */
interface UseServerPaginationOptionsTyped<TEndpoint extends EndpointContract>
        extends UseServerPaginationOptionsBase {
    endpoint: TEndpoint
    /**
     * Empty string is allowed for endpoints whose contract declares
     * {@code sort: {}} (no client-controlled sort) — the hook then suppresses
     * the {@code sort=} URL parameter so the backend's {@code @SortableFields({})}
     * runtime check doesn't reject the request. Empty-sort contracts also
     * apply when the page paginates client-side or when the registry is
     * intrinsically ordered (e.g. Types' alphabetic sort).
     */
    defaultSortBy: TEndpoint['sort'][keyof TEndpoint['sort']] | ''
}

export type UseServerPaginationOptions<TEndpoint extends EndpointContract = EndpointContract> =
    UseServerPaginationOptionsTyped<TEndpoint>

export function useServerPagination<TEndpoint extends EndpointContract>(
    options: UseServerPaginationOptionsTyped<TEndpoint>,
) {
    return useServerPaginationImpl(options as unknown as UseServerPaginationOptionsTyped<EndpointContract>)
}

function resolveNamespace(options: UseServerPaginationOptions): string | undefined {
    return options.namespace ?? options.endpoint.namespace
}

function prefix(name: string, ns?: string): string {
    return ns ? `${ns}.${name}` : name
}

// WHY: when a filter descriptor's name matches the contract's search slot
// (e.g. NAME / `name` on Service Accounts), the URL param is already owned
// by the search text input. Including it in `filters` too would double-bind:
// a chip would render in the bar AND the panel duplicate would appear. The
// single source of truth for that param is `search` / `q`.
function nonSearchFilterEntries(
    endpoint: EndpointContract,
): [string, EndpointContractFilterDescriptor][] {
    const filterMap = endpoint.filter as Record<string, EndpointContractFilterDescriptor>
    const searchParam = endpoint.search?.param
    return Object.entries(filterMap).filter(
        ([, descriptor]) => descriptor.name !== searchParam,
    )
}

/**
 * Strip packed `id|displayName|entityType|subType` URL state down to bare
 * id(s) for `kind: 'uuid'` and `kind: 'csv', element: 'uuid'` filters
 * before they hit the wire — the backend `@RequestParam UUID` /
 * `List<UUID>` binders reject anything beyond the id itself.
 */
function stripPackedUuidIds(
    descriptor: EndpointContractFilterDescriptor,
    value: string,
): string {
    if (descriptor.kind === 'uuid') {
        return unpackEntityLinkId(value)
    }
    if (descriptor.kind === 'csv' && descriptor.element === 'uuid') {
        return unpackEntityLinkIdsCsv(value)
    }
    return value
}

function useServerPaginationImpl(options: UseServerPaginationOptions) {
    const {paginationConfig} = useAppConfig()
    const configPageSize = paginationConfig?.defaultPageSize ?? DEFAULT_PAGINATION_CONFIG.defaultPageSize
    const pageSizeOptions = paginationConfig?.pageSizeOptions ?? DEFAULT_PAGINATION_CONFIG.pageSizeOptions

    const endpoint = options.endpoint
    const defaultSortBy = options.defaultSortBy
    const defaultSortDir = options.defaultSortDir ?? SortDir.ASC
    const defaultPageSize = options.defaultPageSize ?? configPageSize
    const sortable = options.sortable ?? false
    const namespace = resolveNamespace(options)
    const qParam = endpoint?.search?.param ?? PARAM_Q_DEFAULT

    const [searchParams, setSearchParams] = useSearchParams()
    // sessionStorage-backed filter persistence (issue 342.23). The hook is
    // self-managed via effects on the URL — no return value to consume here.
    // On mount it either writes URL params to storage (URL wins) or restores
    // them from storage when the URL is empty (e.g. after navigating to a
    // detail page and back).
    useFilterStatePersistence()

    // Legacy `search` param stays readable so pages not yet migrated keep
    // working; new `q` param (or the contract's search.param) is preferred
    // when the contract declares a search slot.
    const legacySearch = searchParams.get(prefix(PARAM_SEARCH, namespace)) || ''
    const qValue = searchParams.get(prefix(qParam, namespace)) || ''
    const search = endpoint?.search ? qValue : legacySearch

    const page = parseInt(searchParams.get(prefix(PARAM_PAGE, namespace)) || '0', 10)
    const pageSize = parseInt(searchParams.get(prefix(PARAM_SIZE, namespace)) || String(defaultPageSize), 10)

    const sortBy = sortable
        ? (searchParams.get(prefix(PARAM_SORT_BY, namespace)) || defaultSortBy)
        : defaultSortBy
    const sortDir = sortable
        ? ((searchParams.get(prefix(PARAM_SORT_DIR, namespace)) as SortDirection) || defaultSortDir)
        : defaultSortDir

    const [searchInput, setSearchInput] = useState(search)

    // Keep local edit buffer synced when the URL changes externally (e.g.,
    // browser back/forward) — otherwise the input would silently drift away
    // from the committed URL state.
    const lastSync = useRef(search)
    useEffect(() => {
        if (search !== lastSync.current) {
            lastSync.current = search
            setSearchInput(search)
        }
    }, [search])

    const filters: Record<string, string | string[]> = useMemo(() => {
        if (!endpoint) return {}
        const out: Record<string, string | string[]> = {}
        for (const [key, descriptor] of nonSearchFilterEntries(endpoint)) {
            const paramName = prefix(descriptor.name, namespace)
            const raw = searchParams.get(paramName)
            if (raw === null || raw === '') continue
            if (descriptor.kind === 'csv') {
                out[key] = raw.split(',').filter(s => s.length > 0)
            } else {
                out[key] = raw
            }
        }
        return out
    }, [endpoint, searchParams, namespace])

    const dynamicAttributes: Record<string, string | string[]> = useMemo(() => {
        const out: Record<string, string | string[]> = {}
        const attrPrefix = prefix(PARAM_ATTR_PREFIX, namespace)
        for (const [name, value] of searchParams.entries()) {
            if (!name.startsWith(attrPrefix)) continue
            const attrName = name.slice(attrPrefix.length)
            if (value.includes(',')) {
                out[attrName] = value.split(',').filter(s => s.length > 0)
            } else {
                out[attrName] = value
            }
        }
        return out
    }, [searchParams, namespace])

    const searchAndFilters: SearchAndFilters = useMemo(() => ({
        q: search,
        filters,
        dynamicAttributes,
    }), [search, filters, dynamicAttributes])

    const updateParams = useCallback((updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams)
        const pageKey = prefix(PARAM_PAGE, namespace)
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key)
            } else {
                newParams.set(key, value)
            }
        })
        if (!(pageKey in updates)) {
            newParams.set(pageKey, '0')
        }
        setSearchParams(newParams)
    }, [searchParams, setSearchParams, namespace])

    const submitSearch = useCallback(() => {
        // Prefer the new `q` param when the contract declares search; fall
        // back to legacy `search` so un-migrated pages stay on their existing
        // URL shape.
        const paramName = endpoint?.search ? prefix(qParam, namespace) : prefix(PARAM_SEARCH, namespace)
        updateParams({[paramName]: searchInput})
    }, [updateParams, searchInput, endpoint, qParam, namespace])

    const handleSearchSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault()
        submitSearch()
    }, [submitSearch])

    const handleClearSearch = useCallback(() => {
        setSearchInput('')
        const paramName = endpoint?.search ? prefix(qParam, namespace) : prefix(PARAM_SEARCH, namespace)
        updateParams({[paramName]: null})
    }, [updateParams, endpoint, qParam, namespace])

    /**
     * Commit a new search + filter combination to the URL atomically.
     * Resets `page` to 0 but preserves `sort` / `sortDir` (FE-39 sort stability).
     */
    const setSearchAndFilters = useCallback((next: Partial<SearchAndFilters>) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            const searchParamName = endpoint?.search ? prefix(qParam, namespace) : prefix(PARAM_SEARCH, namespace)

            if ('q' in next) {
                if (next.q && next.q.length > 0) {
                    newParams.set(searchParamName, next.q)
                } else {
                    newParams.delete(searchParamName)
                }
            }

            if ('filters' in next && next.filters !== undefined && endpoint) {
                for (const [key, descriptor] of nonSearchFilterEntries(endpoint)) {
                    const paramName = prefix(descriptor.name, namespace)
                    const raw = next.filters[key]
                    if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
                        newParams.delete(paramName)
                        continue
                    }
                    const serialized = Array.isArray(raw) ? raw.filter(v => v !== '').join(',') : raw
                    if (serialized === '') {
                        newParams.delete(paramName)
                    } else {
                        newParams.set(paramName, serialized)
                    }
                }
            }

            if ('dynamicAttributes' in next && next.dynamicAttributes !== undefined) {
                const attrPrefix = prefix(PARAM_ATTR_PREFIX, namespace)
                // Drop all existing attr.* first, then re-apply the new map.
                const keysToDelete: string[] = []
                for (const key of newParams.keys()) {
                    if (key.startsWith(attrPrefix)) keysToDelete.push(key)
                }
                keysToDelete.forEach(k => newParams.delete(k))
                for (const [attrName, raw] of Object.entries(next.dynamicAttributes)) {
                    if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) continue
                    const serialized = Array.isArray(raw) ? raw.filter(v => v !== '').join(',') : raw
                    if (serialized === '') continue
                    newParams.set(`${attrPrefix}${attrName}`, serialized)
                }
            }

            // Reset to page 0 whenever search / filters change — matches the
            // existing `updateParams` behaviour and FE-39 sort stability rule
            // (page resets, sort is preserved).
            newParams.set(prefix(PARAM_PAGE, namespace), '0')

            return newParams
        })
    }, [setSearchParams, endpoint, qParam, namespace])

    const setSort = useCallback((key: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            const sortByKey = prefix(PARAM_SORT_BY, namespace)
            const sortDirKey = prefix(PARAM_SORT_DIR, namespace)
            const currentSortBy = prev.get(sortByKey) || defaultSortBy
            const currentSortDir = (prev.get(sortDirKey) as SortDirection) || defaultSortDir

            if (currentSortBy === key) {
                next.set(sortDirKey, currentSortDir === 'asc' ? 'desc' : 'asc')
            } else {
                next.set(sortByKey, key)
                next.set(sortDirKey, 'asc')
            }
            return next
        })
    }, [setSearchParams, defaultSortBy, defaultSortDir, namespace])

    const setPage = useCallback((newPage: number) => {
        updateParams({[prefix(PARAM_PAGE, namespace)]: newPage.toString()})
    }, [updateParams, namespace])

    const setPageSize = useCallback((newSize: number) => {
        updateParams({[prefix(PARAM_SIZE, namespace)]: newSize.toString(), [prefix(PARAM_PAGE, namespace)]: '0'})
    }, [updateParams, namespace])

    const queryString = useMemo(() => {
        const params = new URLSearchParams()
        // Legacy `search` remains supported for un-migrated pages; contracts
        // with a `search` slot switch over to their declared param name.
        if (search) {
            const name = endpoint?.search ? qParam : PARAM_SEARCH
            params.set(name, search)
        }
        if (endpoint) {
            for (const [key, descriptor] of nonSearchFilterEntries(endpoint)) {
                const raw = filters[key]
                if (raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) continue
                const joined = Array.isArray(raw) ? raw.join(',') : raw
                const serialized = stripPackedUuidIds(descriptor, joined)
                if (serialized) params.set(descriptor.name, serialized)
            }
            for (const [attrName, raw] of Object.entries(dynamicAttributes)) {
                const serialized = Array.isArray(raw) ? raw.join(',') : raw
                if (serialized) params.set(`attr.${attrName}`, serialized)
            }
        }
        params.set('page', page.toString())
        params.set('size', pageSize.toString())
        // Empty defaultSortBy signals an @SortableFields({}) endpoint where the backend
        // applies a fixed ordering itself. Sending sort= would be rejected by the runtime
        // validator, so skip emitting the parameter entirely.
        if (sortBy) {
            params.set('sort', `${sortBy},${sortDir}`)
        }
        return params.toString()
    }, [search, filters, dynamicAttributes, page, pageSize, sortBy, sortDir, endpoint, qParam])

    return {
        search,
        searchInput,
        setSearchInput,
        searchAndFilters,
        setSearchAndFilters,
        page,
        pageSize,
        sortBy,
        sortDir,
        setSort,
        setPage,
        setPageSize,
        pageSizeOptions,
        queryString,
        updateParams,
        submitSearch,
        handleSearchSubmit,
        handleClearSearch,
        namespace,
    }
}
